/**
 * Builds the PATCH that saves one resource page. Other systems write these
 * resources too, so a save sets the dirty fields on a copy and lets
 * Medplum's `createPatch` compute the smallest patch: every element the
 * schema does not own survives. A `versionId` test goes first, so a save
 * made over someone else's newer write is rejected instead of erasing it.
 *
 * The Kora config does not save through here. Kora owns its whole `Basic`,
 * so its patch replaces the extension array instead.
 */
import type { PatchOperation } from "@medplum/core";
import type { Extension } from "@medplum/fhirtypes";

import type {
  ConfigResource,
  ConfigSchema,
} from "@/types/clinic-configuration";
import { createPatch } from "@/lib/utils/fhir";

import {
  buildFieldExtension,
  fromSectionDraft,
  isEditableField,
  isSection,
  isWritableField,
} from "./configuration-schema-runtime";
import { getResourceShape } from "./resource-draft";
import { writePropertyField } from "./resource-path";
import { mergeSectionExtensions } from "./section-merge";

/**
 * Every root extension with `url` gives way to `replacement`, placed where
 * the first one sat, so a save never reorders the other extensions. With
 * none stored, `replacement` goes last.
 */
export const replaceExtensionRun = (
  extensions: Extension[],
  url: string,
  replacement: Extension[]
): Extension[] => {
  const firstIndex = extensions.findIndex((extension) => extension.url === url);

  if (firstIndex === -1) {
    return [...extensions, ...replacement];
  }

  return extensions.flatMap((extension, index) => {
    if (extension.url !== url) {
      return [extension];
    }
    return index === firstIndex ? replacement : [];
  });
};

/**
 * FHIR has no empty arrays, so a resource left with no extensions loses the
 * key.
 */
const withExtensions = <Target extends ConfigResource>(
  resource: Target,
  extensions: Extension[]
): Target => {
  const copy = { ...resource };

  if (extensions.length > 0) {
    copy.extension = extensions;
  } else {
    delete copy.extension;
  }

  return copy;
};

const writeExtensionField = <Target extends ConfigResource>(
  resource: Target,
  field: ConfigSchema.ExtensionField,
  value: unknown
): Target => {
  if (!isWritableField(field)) {
    throw new Error(
      `Extension field "${field.slug}" is a weekSchedule, which only a section's own page can write.`
    );
  }

  const extensions =
    value === undefined ? [] : buildFieldExtension(field, value);

  return withExtensions(
    resource,
    replaceExtensionRun(resource.extension ?? [], field.slug, extensions)
  );
};

const writeSection = <Target extends ConfigResource>(
  resource: Target,
  section: ConfigSchema.Section,
  draft: unknown
): Target => {
  const stored = resource.extension ?? [];
  const blocks = mergeSectionExtensions(
    stored.filter(({ url }) => url === section.slug),
    section,
    draft
  );

  return withExtensions(
    resource,
    replaceExtensionRun(stored, section.slug, blocks)
  );
};

/**
 * Dirty names are form paths (`hours.mon.ranges.0.start`). The write goes
 * per field, so only the top-level key counts.
 */
const toDirtyKeys = (dirtyFieldNames: string[]) => [
  ...new Set(dirtyFieldNames.map((name) => name.split(".")[0])),
];

/**
 * `resource` with each dirty, editable field of `draft` applied, as a new
 * object: `resource` itself is never changed. A clean field is never
 * written, so a stored value the schema would write differently stays as
 * it is.
 */
export const applyResourceDraft = <Target extends ConfigResource>(
  resource: Target,
  schema: ConfigSchema.Resource,
  draft: Record<string, unknown>,
  dirtyFieldNames: string[]
): Target => {
  const shape = getResourceShape(schema);
  const extensions = schema.extensions ?? {};

  return toDirtyKeys(dirtyFieldNames).reduce<Target>((edited, key) => {
    const extension = extensions[key];
    if (extension && isSection(extension)) {
      return writeSection(edited, extension, draft[key]);
    }

    const field = shape[key];
    if (!field) {
      throw new Error(`Dirty field "${key}" is not in the schema.`);
    }
    if (!isEditableField(field)) {
      return edited;
    }

    const value = fromSectionDraft({ [key]: field }, {
      [key]: draft[key],
    } as ConfigSchema.SectionDraft<ConfigSchema.Shape>)[key];

    return "path" in field
      ? writePropertyField(edited, field, value)
      : writeExtensionField(edited, field, value);
  }, resource);
};

/**
 * The smallest patch from `stored` to `edited` (`createPatch`), so every
 * element the edit did not touch survives. A `test` on `/meta/versionId`
 * goes first, with the `versionId` the user loaded. Not
 * `stored.meta.versionId`: `stored` may be a fresher read, and a test
 * against it always passes. The ops address array items by index, which
 * is safe only because of this test.
 */
export const buildVersionedPatchOperations = <Target extends ConfigResource>(
  stored: Target,
  edited: Target,
  versionId: string
): PatchOperation[] => [
  { op: "test", path: "/meta/versionId", value: versionId },
  ...createPatch(stored, edited),
];

/**
 * The patch for one resource save: the dirty fields applied to a copy of
 * `stored` (`applyResourceDraft`), then `buildVersionedPatchOperations`.
 */
export const buildResourcePatchOperations = <Target extends ConfigResource>(
  stored: Target,
  schema: ConfigSchema.Resource,
  draft: Record<string, unknown>,
  dirtyFieldNames: string[],
  versionId: string
): PatchOperation[] =>
  buildVersionedPatchOperations(
    stored,
    applyResourceDraft(stored, schema, draft, dirtyFieldNames),
    versionId
  );
